/* eslint-disable indent */
import { useMemo } from 'react';
import * as yup from 'yup';

const getRequiredFieldsSet = (aiQuestionare) => new Set(
    aiQuestionare
        .flatMap((section) => section.questions)
        .map((q) => q.input_name),
);

export const useValidationSchema = (aiQuestionare = []) => {
    const requiredFields = useMemo(
        () => getRequiredFieldsSet(aiQuestionare),
        [aiQuestionare],
    );

    const req = (path) => (requiredFields.has(path)
        ? yup.string().required('Campo obbligatorio')
        : yup.string().nullable());

    return yup.object({
        complete_questionario: yup.boolean().required(),

        location_and_structures: yup.object().when('complete_questionario', {
            is: true,
            then: () => yup.object({
                proximity_to_water_bodies: req('location_and_structures.proximity_to_water_bodies'),
                proximity_to_fragile_slopes_or_landslide_areas: req('location_and_structures.proximity_to_fragile_slopes_or_landslide_areas'),
                elevation_above_ground_level: req('location_and_structures.elevation_above_ground_level'),
                proximity_to_major_transport_routes: req('location_and_structures.proximity_to_major_transport_routes'),
                presence_of_groundwater: req('location_and_structures.presence_of_groundwater'),
                presence_of_basements_or_semi_basements: req('location_and_structures.presence_of_basements_or_semi_basements'),
                presence_of_shelving: req('location_and_structures.presence_of_shelving'),
                combustible_building_materials: req('location_and_structures.combustible_building_materials'),
                roof_supporting_structure_material: req('location_and_structures.roof_supporting_structure_material'),
            }),
            otherwise: () => yup.object().strip(),
        }),

        infrastructure_and_machines: yup.object().when('complete_questionario', {
            is: true,
            then: () => yup.object({
                electrical_system_pre_1990: req('infrastructure_and_machines.electrical_system_pre_1990'),
                damage_risk_to_equipment_due_to_power_outage: req('infrastructure_and_machines.damage_risk_to_equipment_due_to_power_outage'),
                damage_risk_to_computers_due_to_power_outage: req('infrastructure_and_machines.damage_risk_to_computers_due_to_power_outage'),
                it_system_essential_for_continuity: req('infrastructure_and_machines.it_system_essential_for_continuity'),
                presence_of_combustible_packaging: req('infrastructure_and_machines.presence_of_combustible_packaging'),
                presence_of_expanded_plastic_packaging: req('infrastructure_and_machines.presence_of_expanded_plastic_packaging'),
                staff_present_during_closure: req('infrastructure_and_machines.staff_present_during_closure'),
                firefighter_response_time_under_15_minutes: req('infrastructure_and_machines.firefighter_response_time_under_15_minutes'),
                staff_present_at_night: req('infrastructure_and_machines.staff_present_at_night'),
                use_of_chemicals: req('infrastructure_and_machines.use_of_chemicals'),
                concrete_or_resin_flooring: req('infrastructure_and_machines.concrete_or_resin_flooring'),
                use_of_open_flames: req('infrastructure_and_machines.use_of_open_flames'),
            }),
            otherwise: () => yup.object().strip(),
        }),

        security_and_logistics: yup.object().when('complete_questionario', {
            is: true,
            then: () => yup.object({
                frequent_access_by_non_staff: req('security_and_logistics.frequent_access_by_non_staff'),
                third_party_access_for_installations_or_services: req('security_and_logistics.third_party_access_for_installations_or_services'),
            }),
            otherwise: () => yup.object().strip(),
        }),

        product_and_its: yup.object().when('complete_questionario', {
            is: true,
            then: () => yup.object({
                perishable_products: req('product_and_its.perishable_products'),
                company_branding_on_products: req('product_and_its.company_branding_on_products'),
                critical_business_documents: req('product_and_its.critical_business_documents'),
                disaster_recovery_and_business_continuity_system: req('product_and_its.disaster_recovery_and_business_continuity_system'),
                remote_access_to_information_system: req('product_and_its.remote_access_to_information_system'),
                backup_communication_system: req('product_and_its.backup_communication_system'),
            }),
            otherwise: () => yup.object().strip(),
        }),

        finance_and_personals: yup.object().when('complete_questionario', {
            is: true,
            then: () => yup.object({
                seasonal_or_periodic_revenue_fluctuations: req('finance_and_personals.seasonal_or_periodic_revenue_fluctuations'),
                key_personnel_for_strategy: req('finance_and_personals.key_personnel_for_strategy'),
                key_personnel_for_production: req('finance_and_personals.key_personnel_for_production'),
                key_personnel_with_exclusive_client_relationships: req('finance_and_personals.key_personnel_with_exclusive_client_relationships'),
                defined_organizational_chart_and_governance: req('finance_and_personals.defined_organizational_chart_and_governance'),
            }),
            otherwise: () => yup.object().strip(),
        }),
    });
};
